/**
 * ENGAGEMENT: the counts a song carries, and the two scores the Home page ranks by.
 *
 * THREE FACTS, AND THEY ARE NOT INTERCHANGEABLE:
 *
 *   isLoved     whether the CURRENT USER has Loved this song. That is `Song.favorite` —
 *               the flag the Library's star and the lyrics dialog's Like already set. It
 *               is a boolean about one person.
 *   loveCount   how many Loves the song has IN TOTAL. An aggregate about everybody.
 *   playCount   how many times the song has been played.
 *
 * And two more that are not fields the ranking always owns itself:
 *
 *   saves       playlist memberships, passed in as a map
 *   shares      share actions recorded on the song
 *
 * Conflating the first two is the classic bug: a heart that fills in but shows the same
 * number, or a number that moves for everyone because one person pressed a button. They
 * are read through two different functions below and stored in two different fields.
 *
 * WHY loveCount EXCLUDES THE VIEWER. `Song.loveCount` is what the backend reports, which
 * on a real server means "Loves from every account". The viewer's own Love lives in
 * `favorite`, which the client owns and can flip instantly. `loveCountFor` adds them:
 *
 *     loveCountFor(song) = (song.loveCount ?? 0) + (song.favorite ? 1 : 0)
 *
 * So pressing Love moves the displayed total by exactly one, immediately, with no
 * optimistic write to an aggregate the client has no right to set — and when the backend
 * starts reporting a real `loveCount`, the arithmetic is already correct.
 *
 * DEMO MODE IS AN EXPLICIT EXCEPTION. With VITE_LYRIXA_DEMO_STATS enabled, the legacy
 * admin songs explicitly listed in demoSongStats.json keep their configured baseline. Every
 * discoverable song also receives deterministic elapsed growth, and these readers add genuine
 * activity on top. None of the simulation is written to Song, so disabling the flag returns
 * purely real counts.
 *
 * Pure functions, no React and no stores, so lib/discovery can rank with them and the
 * cards can render with them without a second path.
 */
import type { Song } from '../types/domain';
import { demoSongStatOverrideFor } from './demoSongStats';

export interface FeaturedSignalCounts {
  loves: number;
  plays: number;
  saves: number;
  shares?: number;
}

/**
 * Plays recorded for a song. Absent counts as none.
 *
 * Outside demo mode this is the stored real count. In demo mode, discoverable songs show their
 * simulated projection plus that stored count.
 */
export function playCountFor(song: Song): number {
  const baseline = demoSongStatOverrideFor(song)?.playCount ?? 0;
  return baseline + (song.playCount ?? 0);
}

/**
 * Total Loves for a song, INCLUDING the viewer's own.
 *
 * The backend's aggregate plus this user's flag — see the header on why that sum is the
 * whole design. Demo mode also adds the song's simulated projection first.
 */
export function loveCountFor(song: Song): number {
  const baseline = demoSongStatOverrideFor(song)?.loveCount ?? 0;
  return baseline + (song.loveCount ?? 0) + (song.favorite ? 1 : 0);
}

/** Whether the CURRENT USER has Loved this song. Separate from the count above. */
export function isLoved(song: Song): boolean {
  return song.favorite;
}

/**
 * SAVES — the one engagement signal a `Song` does not carry, and does not need to.
 *
 * A save in this app is "add to a playlist": the lyrics dialog's Save button and the
 * Library card's "Add to a playlist" both do exactly that, and a playlist holds song ids.
 * So how many times a song has been saved is already a countable fact — how many playlists
 * contain it. There is no `Song.saveCount`, and adding one would be a second record of
 * something the playlists state exactly, kept in sync by hand.
 *
 * WHICH IS WHY IT ARRIVES AS A MAP. lib/discovery is pure and is handed songs; playlists
 * live in their own store. The one caller that holds both — hooks/useShowcase — builds this
 * with stores/playlistsStore.songSaveCounts and passes it down. An absent map, or a song
 * with no entry in it, means zero saves: that is the truth for a library with no playlists,
 * not a placeholder standing in for a number nobody has.
 */
export type SaveCounts = Readonly<Record<string, number>>;

/** Saves recorded for a song, plus its simulated projection when enabled. */
export function saveCountFor(song: Song, saves: SaveCounts = {}): number {
  const baseline = demoSongStatOverrideFor(song)?.saveCount ?? 0;
  return baseline + (saves[song.id] ?? 0);
}

/**
 * Shares recorded for a song. Absent counts as none.
 *
 * WHAT THIS IS TODAY: shares that actually left this browser — the system sheet was used or
 * the link reached the clipboard, counted in hooks/useShareSong through
 * stores/songsStore.recordShare. A dismissed sheet counts nothing, because nothing was
 * shared. It is not a platform figure and nothing here knows whether a sent link was opened;
 * outside demo mode a card shows 0 until someone shares.
 */
export function shareCountFor(song: Song): number {
  const baseline = demoSongStatOverrideFor(song)?.shareCount ?? 0;
  return baseline + (song.shareCount ?? 0);
}

/**
 * How much weight each signal carries in the Featured score.
 *
 * Featured is the next-day winner of a daily competition, but the score itself is still a
 * plain weighted sum: plays carry the strongest signal there, while Loves, saves and shares
 * are equal supporting signals.
 */
export const FEATURED_WEIGHTS = { play: 60, love: 20, save: 20, share: 20 } as const;

/**
 * How much weight each signal carries in the Trending score.
 *
 * Trending is the running chart of overall engagement so far, and it leans even more heavily
 * on plays than Featured does: repeated listening is the strongest signal of ongoing momentum.
 */
export const TRENDING_WEIGHTS = { play: 80, love: 20, save: 20, share: 20 } as const;

/**
 * THE TRENDING SCORE: weighted overall engagement so far.
 *
 *     (plays * 80) + (loves * 20) + (shares * 20) + (saves * 20)
 */
export function trendingScore(song: Song, saves: SaveCounts = {}): number {
  return trendingScoreFromCounts({
    loves: loveCountFor(song),
    plays: playCountFor(song),
    saves: saveCountFor(song, saves),
    shares: shareCountFor(song),
  });
}

export function trendingScoreFromCounts(counts: FeaturedSignalCounts): number {
  return (
    counts.plays * TRENDING_WEIGHTS.play +
    counts.loves * TRENDING_WEIGHTS.love +
    (counts.shares ?? 0) * TRENDING_WEIGHTS.share +
    counts.saves * TRENDING_WEIGHTS.save
  );
}

/**
 * THE FEATURED SCORE: the weighted daily score used to pick tomorrow's hero.
 *
 *     (plays * 60) + (loves * 20) + (shares * 20) + (saves * 20)
 */
export function featuredScore(song: Song, saves: SaveCounts = {}): number {
  return featuredScoreFromCounts({
    loves: loveCountFor(song),
    plays: playCountFor(song),
    saves: saveCountFor(song, saves),
    shares: shareCountFor(song),
  });
}

export function featuredScoreFromCounts(counts: FeaturedSignalCounts): number {
  return (
    counts.plays * FEATURED_WEIGHTS.play +
    counts.loves * FEATURED_WEIGHTS.love +
    (counts.shares ?? 0) * FEATURED_WEIGHTS.share +
    counts.saves * FEATURED_WEIGHTS.save
  );
}

/**
 * Human count formatting: 0, 24, 1.2K, 3.4M.
 *
 * Rounds DOWN (1,299 → "1.2K"), so the abbreviation never claims more engagement than
 * there is. Under 1,000 the exact number is printed — the app's real counts are all in
 * that range, and this exists for the day they are not rather than to make today's look
 * bigger.
 */
export function formatCount(n: number): string {
  const value = Math.max(0, Math.floor(n));
  if (value < 1_000) return String(value);
  if (value < 1_000_000) return `${trimZero(value / 1_000)}K`;
  return `${trimZero(value / 1_000_000)}M`;
}

/** One decimal place, floored, with a bare ".0" dropped: 1.0 → "1", 1.25 → "1.2". */
function trimZero(n: number): string {
  const oneDecimal = Math.floor(n * 10) / 10;
  return Number.isInteger(oneDecimal) ? String(oneDecimal) : oneDecimal.toFixed(1);
}

/**
 * The Love button's ACCESSIBLE LABEL: "1 Love", "24 Loves".
 *
 * NOT its visible text, which is the bare number beside the heart. The word is deliberately
 * absent on screen — a heart is already the recognisable symbol for the action, and "♡ Love
 * 24" spends a column of a 160px card saying what the icon says — but a screen reader needs
 * to know what the number counts, so the word lives in the label instead. Same split as the
 * play and download counters, which put their word in a `.lyx-visually-hidden` span.
 *
 * Singular at exactly one, because "1 Loves" beside a heart somebody just pressed is the
 * kind of detail that makes a count look generated.
 */
export function loveCountLabel(count: number): string {
  return `${formatCount(count)} ${count === 1 ? 'Love' : 'Loves'}`;
}

/** The Save control's accessible count, in the same shape as `loveCountLabel`. */
export function saveCountLabel(count: number): string {
  return `${formatCount(count)} ${count === 1 ? 'save' : 'saves'}`;
}

/** The Share control's accessible count, in the same shape as `loveCountLabel`. */
export function shareCountLabel(count: number): string {
  return `${formatCount(count)} ${count === 1 ? 'share' : 'shares'}`;
}
